import React from 'react';
import { Zap, Check, X, DollarSign, Maximize } from 'lucide-react';
import { Slot } from '../types';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface SlotCardProps {
  slot: Slot;
  isSelected?: boolean;
  onSelect?: (slotId: number) => void;
  onViewDetails?: (slot: Slot) => void;
  onReserve?: (slot: Slot) => void;
  showCheckbox?: boolean;
}

export default function SlotCard({
  slot,
  isSelected = false,
  onSelect,
  onViewDetails,
  onReserve,
  showCheckbox = false,
}: SlotCardProps) {
  const getSlotTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'premium':
        return 'bg-yellow-100 text-yellow-800';
      case 'preferred':
        return 'bg-moonbone text-ink';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${
        isSelected ? 'ring-2 ring-primary' : ''
      } ${!slot.is_available ? 'opacity-75' : ''}`}
    >
      <div className="p-4">
        {/* Header with checkbox and slot number */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {showCheckbox && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect?.(slot.id)}
                disabled={!slot.is_available}
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
              />
            )}
            <h3 className="text-lg font-bold text-gray-900">{slot.slot_number}</h3>
          </div>
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded ${getSlotTypeColor(
              slot.slot_type
            )}`}
          >
            {slot.slot_type.charAt(0).toUpperCase() + slot.slot_type.slice(1)}
          </span>
        </div>

        {/* Slot details */}
        <div className="space-y-2 mb-4">
          {/* Dimensions */}
          {slot.dimensions && (
            <div className="flex items-center text-sm text-gray-600">
              <Maximize className="h-4 w-4 mr-2" />
              <span>{slot.dimensions}</span>
            </div>
          )}

          {/* Price */}
          <div className="flex items-center text-sm text-gray-600">
            <DollarSign className="h-4 w-4 mr-2" />
            <span className="font-semibold text-primary-dark">{formatCurrency(slot.base_price)}</span> {/* Issue #905: Decimal.js formatting */}
          </div>

          {/* Power availability */}
          <div className="flex items-center text-sm">
            <Zap className={`h-4 w-4 mr-2 ${slot.has_power ? 'text-yellow-500' : 'text-gray-400'}`} />
            <span className={slot.has_power ? 'text-gray-700' : 'text-gray-400'}>
              {slot.has_power ? 'Power Available' : 'No Power'}
            </span>
          </div>

          {/* Availability status */}
          <div className="flex items-center text-sm">
            {slot.is_available ? (
              <>
                <Check className="h-4 w-4 mr-2 text-teal-500" />
                <span className="text-primary-dark font-medium">Available</span>
              </>
            ) : (
              <>
                <X className="h-4 w-4 mr-2 text-red-500" />
                <span className="text-red-700 font-medium">Reserved</span>
              </>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onViewDetails?.(slot)}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Details
          </button>
          {slot.is_available && (
            <button
              onClick={() => onReserve?.(slot)}
              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
            >
              Reserve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
